import { supabase } from "@/integrations/supabase/client";
import type { BlogPost } from "@/types/blog";

export const CATEGORIES = [
  "Fotografia Imobiliária",
  "Tecnologia e IA",
  "Técnicas de Vendas",
  "Mercado Imobiliário",
  "Educação Toc Toc",
  "Tendências & Growth",
];

export const COMMON_TAGS = [
  "imóveis",
  "corretores",
  "fotografia",
  "vendas",
  "tecnologia",
  "mercado imobiliário",
  "dicas",
  "tendências",
  "marketing digital",
  "inovação",
];

const BLOG_TABLE = "blog_posts";
const PUBLIC_BLOG_VIEW = "blog_posts_public";

async function listPublished(): Promise<BlogPost[]> {
  const { data, error } = await supabase
    .from(PUBLIC_BLOG_VIEW)
    .select("*")
    .order("published_at", { ascending: false });
  if (error) throw error;
  return (data as BlogPost[]) ?? [];
}

async function getBySlug(slug: string): Promise<BlogPost | null> {
  const { data, error } = await supabase
    .from(PUBLIC_BLOG_VIEW)
    .select("*")
    .eq("slug", slug)
    .single();
  if (error) return null;
  return data as BlogPost;
}

async function listAllAdmin(): Promise<BlogPost[]> {
  const { data, error } = await supabase
    .from(BLOG_TABLE)
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data as BlogPost[]) ?? [];
}

async function listAll(): Promise<BlogPost[]> {
  return listAllAdmin();
}

async function getById(id: string): Promise<BlogPost | null> {
  const { data, error } = await supabase
    .from(BLOG_TABLE)
    .select("*")
    .eq("id", id)
    .single();
  if (error) return null;
  return data as BlogPost;
}

function buildAdminPayload(post: BlogPost) {
  return {
    ...post,
    featured_image: post.cover_url || post.featured_image,
    published_at:
      post.status === "published" ? post.published_at ?? new Date().toISOString() : null,
  };
}

async function upsertAdmin(post: BlogPost): Promise<string | null> {
  const payload = buildAdminPayload(post);
  const { data, error } = await supabase
    .from(BLOG_TABLE)
    .upsert(payload)
    .select("id")
    .single();
  if (error) throw error;
  return data?.id ?? null;
}

async function removeAdmin(id: string) {
  const { error } = await supabase.from(BLOG_TABLE).delete().eq("id", id);
  if (error) throw error;
}

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

async function getAllPosts() {
  return listAllAdmin();
}

async function getPostById(id: string) {
  return getById(id);
}

async function getFeaturedPost() {
  const posts = await listPublished();
  return posts[0] ?? null;
}

async function createPost(post: BlogPost) {
  const id = await upsertAdmin(post);
  return id ? getById(id) : null;
}

async function updatePost(id: string, updates: Partial<BlogPost>) {
  const nextId = await upsertAdmin({ ...updates, id } as BlogPost);
  return nextId ? getById(nextId) : null;
}

async function deletePost(id: string) {
  await removeAdmin(id);
  return true;
}

function initialize() {
  return Promise.resolve();
}

export const blogService = {
  listPublished,
  listAll,
  listAllAdmin,
  getBySlug,
  upsert: upsertAdmin,
  upsertAdmin,
  remove: removeAdmin,
  removeAdmin,
  getById,
  getAllPosts,
  getPostById,
  getPostBySlug: getBySlug,
  getFeaturedPost,
  createPost,
  updatePost,
  deletePost,
  generateSlug,
  listPosts: listAll,
  initialize,
};

export type { BlogPost };
export default blogService;
